import React, { useEffect, useMemo, useState } from "react";
import { Empty, Modal as AntdModal, Spin, Tag } from "antd";
import moment from "moment";

import { getEmployeeBalanceDetail } from "../../../../../../../services/attendanceService";
import { getErrorMessage } from "../../../../../../../core/data/redux/utils/errorUtils";
import { notify } from "../../../../../../../services/toast.config";

import type { AdjustmentRow } from "../types";

type EmployeeAdjustmentModalProps = {
  open: boolean;
  userId: number | null;
  userName: string | null;
  leaveTypeId: number | null;
  leaveTypeName: string | null;
  year: number;
  onClose: () => void;
};

const sharedModalStyles = {
  mask: {
    position: "fixed" as const,
    inset: 0,
    width: "100vw",
    height: "100vh",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    background: "rgba(0, 0, 0, 0.45)",
    zIndex: 9998,
  },
  content: {
    padding: 0,
    display: "flex" as const,
    flexDirection: "column" as const,
    maxHeight: "90vh",
    overflow: "hidden",
    borderRadius: 12,
  },
  header: {
    flexShrink: 0,
    padding: "16px 24px 12px",
    borderBottom: "1px solid #f0f0f0",
    marginBottom: 0,
  },
  body: {
    flex: 1,
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
    padding: "20px 24px 8px",
  },
  footer: {
    flexShrink: 0,
    padding: "12px 24px 16px",
    borderTop: "1px solid #f0f0f0",
    marginTop: 0,
  },
};

export const EmployeeAdjustmentModal: React.FC<EmployeeAdjustmentModalProps> = ({
  open,
  userId,
  userName,
  leaveTypeId,
  leaveTypeName,
  year,
  onClose,
}) => {
  const [adjustments, setAdjustments] = useState<AdjustmentRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;

    let cancelled = false;
    const fetchDetail = async () => {
      setLoading(true);
      setAdjustments([]);
      try {
        const res = await getEmployeeBalanceDetail({ user_id: userId, year });
        if (cancelled) return;
        setAdjustments(res?.data?.data?.recentAdjustments || []);
      } catch (err) {
        if (cancelled) return;
        notify.error(getErrorMessage(err));
        setAdjustments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [open, userId, year]);

  const filtered = useMemo(
    () =>
      leaveTypeId
        ? adjustments.filter((a) => a.leaveTypeId === leaveTypeId)
        : adjustments,
    [adjustments, leaveTypeId],
  );

  const titleSuffix = leaveTypeName ? ` · ${leaveTypeName}` : "";

  return (
    <AntdModal
      open={open}
      onCancel={onClose}
      centered
      width={680}
      maskClosable={false}
      keyboard={false}
      destroyOnClose
      zIndex={9999}
      getContainer={() => document.body}
      styles={sharedModalStyles}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
            Adjustment History — {userName || ""}
            {titleSuffix}
          </span>
          {!loading && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "1px 8px",
                borderRadius: 10,
                background: "#dbeafe",
                color: "#1e40af",
                border: "1px solid #bfdbfe",
              }}
            >
              {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
            </span>
          )}
        </div>
      }
      footer={
        <div style={{ textAlign: "right" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="text-center py-5">
          <Spin />
          <div className="text-muted small mt-2">Loading history…</div>
        </div>
      ) : filtered.length === 0 ? (
        <Empty
          description={
            leaveTypeName
              ? `No manual adjustments recorded for ${leaveTypeName} in ${year}`
              : `No manual adjustments recorded for this year`
          }
        />
      ) : (
        <div>
          <div className="mb-3 text-muted small">
            Showing the most recent {filtered.length} adjustment(s) for {year}.
          </div>
          {filtered.map((adj) => (
            <div className="card border mb-2" key={adj.id}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="fw-semibold">
                    {adj.delta > 0 ? `+${adj.delta}` : adj.delta} day(s) —{" "}
                    {adj.leaveTypeName || `Type #${adj.leaveTypeId}`}
                  </div>
                  <Tag
                    color={adj.appliedTo === "balance_only" ? "blue" : "purple"}
                  >
                    {adj.appliedTo}
                  </Tag>
                </div>
                <div className="text-muted small mt-1">
                  {adj.monthKey ? `Month ${adj.monthKey}` : "Year-level"} ·{" "}
                  {moment(adj.createdAt).format("DD MMM YYYY HH:mm")} ·{" "}
                  {adj.performedByUserName ||
                    `User #${adj.performedByUserId}`}
                </div>
                <div className="mt-2">{adj.reason}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AntdModal>
  );
};

export default EmployeeAdjustmentModal;
